import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { fetchPlaylists, createPlaylist, deletePlaylist } from "../../lib/api";
import { toast } from "sonner";
import { ListMusic, Trash2, Play, Loader2, Save } from "lucide-react";
import { useKaraoke } from "../../store/KaraokeStore";

export const SavePlaylistDialog = ({ open, onOpenChange }) => {
  const { queue } = useKaraoke();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    if (!name.trim()) {
      toast.error("Ingresa un nombre");
      return;
    }
    setSaving(true);
    try {
      await createPlaylist(name.trim(), queue);
      toast.success(`Lista "${name}" guardada`);
      setName("");
      onOpenChange(false);
    } catch (e) {
      toast.error("Error al guardar la lista");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border max-w-md" data-testid="save-playlist-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
            <Save className="w-5 h-5 text-secondary" />
            Guardar Cola como Lista
          </DialogTitle>
          <DialogDescription>
            Guarda las {queue.length} canciones actuales para reproducirlas después.
          </DialogDescription>
        </DialogHeader>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nombre de la lista (ej. Fiesta Sabado)"
          data-testid="playlist-name-input"
          autoFocus
          onKeyDown={(e) => e.key === "Enter" && onSave()}
          className="bg-background border-border focus-visible:ring-primary font-mono"
        />
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-border">
            Cancelar
          </Button>
          <Button
            onClick={onSave}
            disabled={saving}
            data-testid="confirm-save-playlist-button"
            className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-bold uppercase tracking-wide"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const PlaylistsDialog = ({ open, onOpenChange }) => {
  const { loadFromPlaylist } = useKaraoke();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchPlaylists();
      setPlaylists(data);
    } catch (e) {
      toast.error("Error al cargar listas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const onDelete = async (id) => {
    try {
      await deletePlaylist(id);
      toast.success("Lista eliminada");
      load();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const onLoad = (pl) => {
    loadFromPlaylist(pl.songs);
    toast.success(`"${pl.name}" cargada en la cola`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border max-w-lg max-h-[80vh]" data-testid="playlists-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
            <ListMusic className="w-5 h-5 text-primary" />
            Mis Listas
          </DialogTitle>
          <DialogDescription>Carga una lista guardada en la cola actual.</DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[55vh] -mx-6 px-6">
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" /></div>
          ) : playlists.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">
              No tienes listas guardadas todavía.
            </div>
          ) : (
            <div className="space-y-2">
              {playlists.map((pl, idx) => (
                <div
                  key={pl.id}
                  data-testid={`playlist-item-${idx}`}
                  className="flex items-center gap-3 border border-border rounded-md p-3 hover:border-primary/40 transition-colors"
                >
                  <div className="w-10 h-10 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                    <ListMusic className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{pl.name}</p>
                    <p className="text-xs text-muted-foreground font-mono">
                      {pl.songs.length} canciones
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => onLoad(pl)}
                    data-testid={`load-playlist-${idx}`}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 h-8"
                  >
                    <Play className="w-3.5 h-3.5 mr-1" fill="currentColor" />
                    Cargar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(pl.id)}
                    data-testid={`delete-playlist-${idx}`}
                    className="text-muted-foreground hover:text-destructive h-8 w-8 p-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
